<script>
import {defineComponent, h} from "vue";
import {VideoCalibrationCategory} from "@/utils/utils";

export default defineComponent({
  name: "draw-canvas",
  props: {
    strokeType: {
      type: String,
      validator: (value) => {
        return ['dash', 'line', 'square', 'circle', 'triangle', 'half_triangle', 'dot', 'polygon', 'none'].indexOf(value) !== -1
      },
      default: 'dash'
    },
    selectedCategory: {
      type: String,
      required: true
    },
    fillShape: {
      type: Boolean,
      default: false
    },
    width: {
      type: [String, Number],
      default: 600
    },
    height: {
      type: [String, Number],
      default: 400
    },
    image: {
      type: String,
      default: ''
    },
    eraser: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: '#000000'
    },
    guidesColor: {
      type: String,
      default: '#a2a5f8'
    },
    lineWidth: {
      type: Number,
      default: 5
    },
    lineCap: {
      type: String,
      validator: (value) => {
        return ['round', 'square', 'butt'].indexOf(value) !== -1
      },
      default: 'round'
    },
    lineJoin: {
      type: String,
      validator: (value) => {
        return ['miter', 'round', 'bevel'].indexOf(value) !== -1
      },
      default: 'miter'
    },
    lock: {
      type: Boolean,
      default: false
    },
    styles: {
      type: [Array, String, Object],
    },
    classes: {
      type: [Array, String, Object],
    },
    backgroundColor: {
      type: String,
      default: '#FFFFFF'
    },
    backgroundImage: {
      type: String,
      default: null
    },
    watermark: {
      type: Object,
      default: null
    },
    saveAs: {
      type: String,
      validator: (value) => {
        return ['jpeg', 'png'].indexOf(value) !== -1
      },
      default: 'png'
    },
    canvasId: {
      type: String,
      default: 'draw-canvas'
    },
    initialImage: {
      type: Array,
      default: () => []
    },
    additionalImages: {
      type: Array,
      default: () => []
    },
    outputWidth: {
      type: Number
    },
    outputHeight: {
      type: Number
    },
    deleteColor: {
      type: String,
      default: '#ff0000'
    }
  },
  data() {
    return {
      loadedImage: null,
      drawing: false,
      context: null,
      images: [],
      strokes: {
        id: '',
        type: '',
        from: {x: 0, y: 0},
        coordinates: [],
        color: '',
        width: '',
        fill: false,
        lineCap: '',
        lineJoin: '',
        text: '',
        textPosition: {x: 0, y: 0},
        renderedDimensions: null,
        newDimensions: null,
        category: ''
      },
      guides: [],
      trash: [],
      polygonStarted: false,
      // Crossing Config data
      crossingOverlayPoints: [],
      crossingOverlayColor: null,
      crossingZones: [],
      crossingWaitingAreas: [],
      crossingCanvasWidth: 0,
      crossingCanvasHeight: 0,
      // World rect vertex editing
      editingVertex: null,
      hoveredVertex: null
    };
  },
  mounted() {
    this.images = []
    this.setContext();
    this.$nextTick(() => {
      this.drawInitialImage()
    })
  },
  watch: {
    backgroundImage() {
      this.loadedImage = null
    },
    selectedCategory() {
      this.redraw(false)
    }
  },
  methods: {
    onHoverOutside(imageId) {
      let index = this.images.findIndex(image => image.id === imageId)
      if (index !== -1) {
        this.images[index].color = this.deleteColor
        this.redraw(false)
      }
    },
    onLeaveOutside(imageId) {
      let index = this.images.findIndex(image => image.id === imageId)
      if (index !== -1) {
        this.images[index].color = this.color
        this.redraw(false)
      }
    },
    onDeleteOutside(imageId) {
      let index = this.images.findIndex(img => img.id === imageId)
      if (index !== -1) {
        this.$emit('onDeleteImage', imageId)
        this.images.splice(index, 1)
        this.redraw(true)
      }
    },
    setInputData(images) {
      this.images = JSON.parse(JSON.stringify(images))
      console.log('Set input data on draw canvas: ', this.images)
      this.redraw(true)
    },
    fireOnDrawFinished() {
      let images = this.images.filter(image => image.type === 'dot' || image.type === 'polygon' || image.type === 'line')
      this.$emit('onDrawDone', images)
    },
    async setContext() {
      let canvas = document.querySelector('#' + this.canvasId);
      console.log('Canvas: ', canvas)
      this.context = this.context ? this.context : canvas.getContext('2d');
      await this.setBackground();
    },
    drawInitialImage() {
      if (this.initialImage && this.initialImage.length > 0) {
        // @ts-ignore
        this.images = [].concat(this.images, this.initialImage)
        this.redraw(true)
      }
    },
    async setBackground() {
      this.clear();
      this.context.fillStyle = this.backgroundColor;
      this.context.fillRect(0, 0, Number(this.width), Number(this.height))

      this.$nextTick(() => {
        this.drawBackgroundImage()
      })
      this.save();
    },
    async drawBackgroundImage() {
      if (!this.loadedImage) {
        return new Promise((resolve, reject) => {
          if (!this.backgroundImage) {
            resolve()
            return;
          }
          const image = new Image();
          image.src = this.backgroundImage;
          image.onload = () => {
            this.context.drawImage(image, 0, 0, Number(this.width), Number(this.height));
            this.loadedImage = image
            resolve();
          }
        })
      } else {
        this.context.drawImage(this.loadedImage, 0, 0, Number(this.width), Number(this.height));
      }
    },
    clear() {
      this.context.clearRect(0, 0, Number(this.width), Number(this.height));
    },
    getCoordinates(event) {
      let x, y;
      if (event.touches && event.touches.length > 0) {
        let canvas = document.querySelector('#' + this.canvasId);
        let rect = canvas.getBoundingClientRect();
        x = (event.touches[0].clientX - rect.left);
        y = (event.touches[0].clientY - rect.top);
      } else {
        x = event.offsetX;
        y = event.offsetY;
      }
      return {
        x: x,
        y: y
      }
    },
    findVertexNear(x, y, threshold = 8) {
      for (let i = 0; i < this.images.length; i++) {
        let image = this.images[i]
        if (image.category !== 'worldRect') continue
        // Check 'from' (vertex index 0)
        let dx = x - image.from.x
        let dy = y - image.from.y
        if (Math.sqrt(dx * dx + dy * dy) <= threshold) {
          return {imageIndex: i, vertexIndex: 0}
        }
        // Check coordinates (vertex index 1..N)
        for (let j = 0; j < image.coordinates.length; j++) {
          dx = x - image.coordinates[j].x
          dy = y - image.coordinates[j].y
          if (Math.sqrt(dx * dx + dy * dy) <= threshold) {
            return {imageIndex: i, vertexIndex: j + 1}
          }
        }
      }
      return null
    },
    startDraw(event) {
      if (!this.lock && !this.eraser && this.strokeType !== 'none') {
        // World rect vertex drag
        if (this.selectedCategory === 'worldRect') {
          let coordinate = this.getCoordinates(event)
          let vertex = this.findVertexNear(coordinate.x, coordinate.y)
          if (vertex) {
            this.editingVertex = vertex
            this.drawing = true
            return
          }
        }
        this.drawing = true;
        if (this.strokeType !== 'polygon' || this.strokes === null || (this.strokes && this.strokes.type !== 'polygon')) {
          let coordinate = this.getCoordinates(event);

          //TODO create id type and number
          let imgs = this.images.filter(image => image.category === this.selectedCategory)
          console.log('IMAGES: ', imgs)
          let id = this.selectedCategory + '_0'
          if (imgs.length > 0) {
            id = this.selectedCategory + '_' + imgs.length
          }

          this.strokes = {
            id: id,
            type: this.eraser ? 'eraser' : this.strokeType,
            from: coordinate,
            coordinates: [],
            color: this.eraser ? this.backgroundColor : this.color,
            width: this.lineWidth,
            fill: this.eraser || this.strokeType === 'dash' || this.strokeType === 'line' ? false : this.fillShape,
            lineCap: this.lineCap,
            lineJoin: this.lineJoin,
            text: this.strokeType === 'dot' ? 'DOT' : null,
            textPosition: this.strokeType === 'dot' ? coordinate : null,
            renderedDimensions: {width: this.width, height: this.height},
            category: this.selectedCategory
          };
          this.guides = [];
        }
      }
    },
    stopDraw(finishDraw, from) {
      console.log('From: ', from)
      if (this.editingVertex) {
        this.editingVertex = null
        this.drawing = false
        this.redraw(true)
        this.fireOnDrawFinished()
        return
      }
      if (finishDraw && this.drawing) {
        this.drawing = false;
        this.trash = [];
        this.guides = []
        this.redraw(true);
        if (this.strokeType === 'polygon') {
          this.strokes = null
        }
        this.fireOnDrawFinished()
      }
      if (this.drawing) {
        console.log('STROKE STOP:', this.strokes)
        if (this.strokeType === 'polygon') {
          if (this.strokes.coordinates.length === 0) {
            this.strokes.coordinates = this.guides
          } else {
            let index = this.strokes.coordinates.findIndex(coordinate => coordinate === this.guides[this.guides.length - 1])
            if (index === -1) {
              this.strokes.coordinates.push(this.guides[this.guides.length - 1])
            }
          }
          let index = this.images.findIndex(image => image.id === this.strokes.id)
          this.strokes.color = this.guidesColor
          if (index === -1) {
            this.images.push(this.strokes);
          }

          this.redraw(true);
        } else {
          this.strokes.coordinates = this.guides.length > 0 ? this.guides : this.strokes.coordinates;
          this.images.push(this.strokes);

          this.redraw(true);
          this.drawing = false;
          this.trash = [];
          this.fireOnDrawFinished()
        }
      } else if (this.eraser) {
        this.deleteImage()
        this.redraw(true);
        this.drawing = false;
        this.trash = [];
        this.fireOnDrawFinished()
      }
    },
    draw(event) {
      if (this.drawing && this.editingVertex) {
        let coordinate = this.getCoordinates(event)
        let img = this.images[this.editingVertex.imageIndex]
        let vi = this.editingVertex.vertexIndex
        if (vi === 0) {
          img.from = coordinate
        } else {
          img.coordinates[vi - 1] = coordinate
        }
        if (img.textPositions && img.textPositions[vi]) {
          img.textPositions[vi] = {x: coordinate.x, y: coordinate.y}
        }
        this.redraw(false)
        return
      }
      if (this.drawing) {
        if (!this.context) {
          this.setContext();
        }
        let coordinate = this.getCoordinates(event);
        if (this.eraser || this.strokeType === 'dash') {
          this.strokes.coordinates.push(coordinate);
          this.drawShape(this.context, this.strokes, false);
        } else {
          if (this.strokeType === 'line') {
            this.guides = [{x: coordinate.x, y: coordinate.y}]
          } else if (this.strokeType === 'polygon') {
            this.guides = [{x: coordinate.x, y: coordinate.y}]
          } else if (this.strokeType === 'square') {
            this.guides = [
              {x: coordinate.x, y: this.strokes.from.y},
              {x: coordinate.x, y: coordinate.y},
              {x: this.strokes.from.x, y: coordinate.y},
              {x: this.strokes.from.x, y: this.strokes.from.y}
            ]
          } else if (this.strokeType === 'triangle') {
            const center = Math.floor((coordinate.x - this.strokes.from.x) / 2) < 0 ? Math.floor((coordinate.x - this.strokes.from.x) / 2) * -1 : Math.floor((coordinate.x - this.strokes.from.x) / 2)
            const width = this.strokes.from.x < coordinate.x ? this.strokes.from.x + center : this.strokes.from.x - center
            this.guides = [
              {x: coordinate.x, y: this.strokes.from.y},
              {x: width, y: coordinate.y},
              {x: this.strokes.from.x, y: this.strokes.from.y}
            ]
          } else if (this.strokeType === 'half_triangle') {
            this.guides = [
              {x: coordinate.x, y: this.strokes.from.y},
              {x: this.strokes.from.x, y: coordinate.y},
              {x: this.strokes.from.x, y: this.strokes.from.y}
            ]
          } else if (this.strokeType === 'circle') {
            let radiusX = this.strokes.from.x - coordinate.x < 0 ? (this.strokes.from.x - coordinate.x) * -1 : this.strokes.from.x - coordinate.x
            this.guides = [
              {
                x: this.strokes.from.x > coordinate.x ? this.strokes.from.x - radiusX : this.strokes.from.x + radiusX,
                y: this.strokes.from.y
              },
              {x: radiusX, y: radiusX}
            ]
          }
          this.drawGuide(true);
        }
      }
    },
    drawGuide(closingPath) {
      this.redraw(true);
      this.$nextTick(() => {
        this.context.strokeStyle = this.guidesColor;
        this.context.lineWidth = 1;
        this.context.lineJoin = this.lineJoin;
        this.context.lineCap = this.lineCap;

        this.context.beginPath();
        this.context.setLineDash([15, 15]);
        if (this.strokes.type === 'circle') {
          this.context.ellipse(this.guides[0].x, this.guides[0].y, this.guides[1].x, this.guides[1].y, 0, 0, Math.PI * 2);
        } else if (this.strokeType === 'polygon') {
          console.log('Draw guide poly')
          if (this.strokes.coordinates && this.strokes.coordinates.length > 0) {
            this.context.moveTo(this.strokes.coordinates[this.strokes.coordinates.length - 1].x, this.strokes.coordinates[this.strokes.coordinates.length - 1].y);
          } else if (this.strokes.from) {
            this.context.moveTo(this.strokes.from.x, this.strokes.from.y);
          }

          this.guides.forEach((coordinate) => {
            this.context.lineTo(coordinate.x, coordinate.y);
          });
          if (closingPath) {
            this.context.closePath();
          }
        } else {
          this.context.moveTo(this.strokes.from.x, this.strokes.from.y);
          this.guides.forEach((coordinate) => {
            this.context.lineTo(coordinate.x, coordinate.y);
          });
          if (closingPath) {
            this.context.closePath();
          }
        }
        this.context.stroke();
      })
    },
    drawShape(context, strokes, closingPath) {
      context.strokeStyle = strokes.color;
      context.fillStyle = strokes.color;
      context.lineWidth = strokes.width;
      context.lineJoin = strokes.lineJoin === undefined ? this.lineJoin : strokes.lineJoin;
      context.lineCap = strokes.lineCap === undefined ? this.lineCap : strokes.lineCap;
      context.beginPath();
      context.setLineDash([]);
      if (strokes.type === 'circle') {
        context.ellipse(strokes.coordinates[0].x, strokes.coordinates[0].y, strokes.coordinates[1].x, strokes.coordinates[1].y, 0, 0, Math.PI * 2);
      } else {
        if (strokes.type === 'dot') {
          context.lineWidth = strokes.width + 2;
        } else {
          context.lineWidth = strokes.width;
        }
        context.moveTo(strokes.from.x, strokes.from.y);
        strokes.coordinates.forEach((stroke) => {
          context.lineTo(stroke.x, stroke.y);
        });
        if (closingPath) {
          context.closePath();
        }
      }
      if (strokes.fill) {
        context.fill();
      } else {
        context.stroke();
      }
    },
    deleteImage() {
      let index = this.images.findIndex(img => img.color === this.deleteColor)
      if (index !== -1) {
        let imageId = this.images[index].id
        this.$emit('onDeleteImage', imageId)
        this.images.splice(index, 1)
      }

      console.log('Delete images: ', this.images)
    },
    reset() {
      if (!this.lock) {
        this.images = [];
        this.strokes = {
          type: '',
          coordinates: [],
          color: '',
          width: '',
          fill: false,
          lineCap: '',
          lineJoin: ''
        };
        this.guides = [];
        this.trash = [];
        this.redraw(true);
        this.fireOnDrawFinished()
      }
    },
    undo() {
      if (!this.lock) {
        let strokes = this.images.pop();
        if (strokes) {
          this.trash.push(strokes);
          this.redraw(true);
        }
      }
    },
    redo() {
      if (!this.lock) {
        let strokes = this.trash.pop();
        if (strokes) {
          this.images.push(strokes);
          this.redraw(true);
        }
      }
    },
    async redraw(output) {
      output = typeof output !== 'undefined' ? output : true;
      await this.setBackground()
          .then(() => {
            let baseCanvas = document.createElement('canvas')
            let baseCanvasContext = baseCanvas.getContext('2d')
            baseCanvas.width = Number(this.width)
            baseCanvas.height = Number(this.height)

            if (baseCanvasContext) {
              this.images.forEach((stroke) => {
                console.log('Image: ', stroke)
                //Include this line if you want to show all drawings on summary
                if (/*this.selectedCategory === 'none' || */stroke.category === this.selectedCategory || (this.selectedCategory === VideoCalibrationCategory.SPEED_LINE && stroke.category === 'measurement_line')) {
                  if (baseCanvasContext) {
                    baseCanvasContext.globalCompositeOperation = stroke.type === 'eraser' ? 'destination-out' : 'source-over'

                    if (stroke.type !== 'circle' || (stroke.type === 'circle' && stroke.coordinates.length > 0)) {
                      this.drawShape(baseCanvasContext, stroke, (!(stroke.type === 'eraser' || stroke.type === 'dash' || stroke.type === 'line' || stroke.category === 'midLine')))
                    }
                    if (stroke.text && stroke.textPosition) {
                      baseCanvasContext.font = "10px Arial";

                      let metrics = baseCanvasContext.measureText(stroke.text)
                      let leftStart = stroke.textPosition.x - metrics.width - 8
                      let xStart = stroke.textPosition.x - metrics.width / 2 - 8
                      let xStop = stroke.textPosition.x + metrics.width / 2 + 8
                      if (stroke.category === 'measurement_line') {

                        let bottomLeft = {x: xStart, y: stroke.textPosition.y + 10}
                        let bottomRight = {x: xStop, y: stroke.textPosition.y + 10}
                        let topRight = {x: xStop, y: stroke.textPosition.y - 8}
                        let topLeft = {x: xStart, y: stroke.textPosition.y - 8}

                        let rectText = {
                          type: 'rect',
                          from: bottomLeft,
                          coordinates: [bottomRight, topRight, topLeft],
                          color: stroke.color,
                          width: this.lineWidth,
                          fill: true,
                          lineCap: this.lineCap,
                          lineJoin: this.lineJoin,
                          text: null,
                          textPosition: null,
                          renderedDimensions: {width: this.width, height: this.height},
                          category: 'line'
                        }

                        this.drawShape(baseCanvasContext, rectText, true)
                      }

                      if (stroke.category === 'measurement_line') {
                        baseCanvasContext.fillStyle = '#000000'
                        baseCanvasContext.fillText(stroke.text, xStart + 8, stroke.textPosition.y + 4);
                      } else {
                        baseCanvasContext.fillStyle = stroke.color
                        baseCanvasContext.fillText(stroke.text, leftStart, stroke.textPosition.y + 4);
                      }

                    }

                    if (stroke.texts && stroke.texts.length > 0 && stroke.textPositions && stroke.textPositions.length > 0) {
                      baseCanvasContext.font = "10px Arial";
                      stroke.texts.forEach((txt, index) => {
                        const text = txt
                        const textPosition = stroke.textPositions[index]

                        let metrics = baseCanvasContext.measureText(text)
                        let left = textPosition.x - metrics.width - 8
                        baseCanvasContext.fillText(text, left, textPosition.y + 4);

                      })
                    }

                    // Draw vertex handles for worldRect
                    if (stroke.category === 'worldRect' && this.selectedCategory === 'worldRect') {
                      let vertices = [stroke.from, ...stroke.coordinates]
                      let imgIdx = this.images.indexOf(stroke)
                      vertices.forEach((v, vi) => {
                        let isHovered = this.hoveredVertex && this.hoveredVertex.imageIndex === imgIdx && this.hoveredVertex.vertexIndex === vi
                        baseCanvasContext.beginPath()
                        baseCanvasContext.arc(v.x, v.y, isHovered ? 6 : 4, 0, Math.PI * 2)
                        baseCanvasContext.fillStyle = isHovered ? '#ffff00' : '#ffffff'
                        baseCanvasContext.fill()
                        baseCanvasContext.strokeStyle = '#000000'
                        baseCanvasContext.lineWidth = 1.5
                        baseCanvasContext.stroke()
                      })
                    }

                  }
                }
              })
              this.context.drawImage(baseCanvas, 0, 0, Number(this.width), Number(this.height))
              // Draw crossing elements overlay
              this.drawCrossingElements()
            }
          })
          .then(() => {
            if (output) {
              this.save();
            }
          });
    },
    async setNewDimension(width, height) {

      console.log('Images Set New: ', this.images)

      this.images.forEach(image => {
        if (image.type === 'dot' || image.type === 'line' || image.type === 'polygon') {

          image.newDimensions = {width: width, height: height}

          let percentageX = (image.newDimensions.width / image.renderedDimensions.width)
          let percentageY = (image.newDimensions.height / image.renderedDimensions.height)
          image.from.x = image.from.x * percentageX
          image.from.y = image.from.y * percentageY
          if (image.coordinates && image.coordinates.length > 0) {
            image.coordinates.forEach(coordinate => {
              coordinate.x = coordinate.x * percentageX
              coordinate.y = coordinate.y * percentageY
            })
          }
          image.renderedDimensions = {width: width, height: height}
          if (image.textPosition) {
            image.textPosition = {x: image.textPosition.x * percentageX, y: image.textPosition.y * percentageY}
          }
        }
      })

      await this.redraw(true)
    },
    save() {
      let canvas = document.querySelector('#' + this.canvasId);
      if (this.watermark) {
        let temp = document.createElement('canvas');
        let ctx = temp.getContext('2d')

        if (ctx) {
          temp.width = Number(this.width);
          temp.height = Number(this.height);
          ctx.drawImage(canvas, 0, 0, Number(this.width), Number(this.height));
        }
      } else {
        let temp = document.createElement('canvas');
        let tempCtx = temp.getContext('2d')
        let tempWidth = this.outputWidth === undefined ? this.width : this.outputWidth
        let tempHeight = this.outputHeight === undefined ? this.height : this.outputHeight
        temp.width = Number(tempWidth)
        temp.height = Number(tempHeight)

        if (tempCtx) {
          tempCtx.drawImage(canvas, 0, 0, Number(tempWidth), Number(tempHeight));
          this.$emit('update:image', temp.toDataURL('image/' + this.saveAs, 1));
          return temp.toDataURL('image/' + this.saveAs, 1);
        }
      }
    },
    isEmpty() {
      return this.images.length <= 0;
    },
    getAllStrokes() {
      return this.images;
    },
    handleMousemove(event) {
      // World rect vertex hover
      if (!this.eraser && this.selectedCategory === 'worldRect') {
        let coordinate = this.getCoordinates(event)
        let vertex = this.findVertexNear(coordinate.x, coordinate.y)
        let canvas = document.querySelector('#' + this.canvasId)
        if (vertex) {
          if (!this.hoveredVertex || this.hoveredVertex.imageIndex !== vertex.imageIndex || this.hoveredVertex.vertexIndex !== vertex.vertexIndex) {
            this.hoveredVertex = vertex
            canvas.style.cursor = 'pointer'
            this.redraw(false)
          }
        } else if (this.hoveredVertex) {
          this.hoveredVertex = null
          canvas.style.cursor = 'default'
          this.redraw(false)
        }
      }
      if (this.images && this.images.length > 0 && this.eraser) {

        let coordinate = this.getCoordinates(event);
        let mouseX = coordinate.x;
        let mouseY = coordinate.y;

        let images = JSON.parse(JSON.stringify(this.images))

        images.forEach(image => {
          if (image.category === this.selectedCategory) {
            if (image.type === 'line' || image.type === 'dot' || image.type === 'polygon') {
              if (image.type === 'dot') {
                if (image.from.x >= mouseX - 2 && image.from.x <= mouseX + 2 && image.from.y >= mouseY - 2 && image.from.y <= mouseY + 2) {
                  image.color = this.deleteColor
                } else {
                  image.color = this.color
                }
              } else if (image.type === 'line') {
                console.log('Line: ', image)
                if (!this.isMouseInLineArea(mouseX, mouseY, image.from.x, image.from.y, image.coordinates[0].x, image.coordinates[0].y)) {
                  return
                }
                let line = {x0: image.from.x, x1: image.coordinates[0].x, y0: image.from.y, y1: image.coordinates[0].y}
                let linePoint = this.linePointNearestMouse(line, mouseX, mouseY);
                let dx = mouseX - linePoint.x;
                let dy = mouseY - linePoint.y;
                let distance = Math.abs(Math.sqrt(dx * dx + dy * dy));
                if (distance < 3) {
                  console.log('INSIDE')
                  image.color = this.deleteColor
                } else {
                  image.color = this.color
                  console.log('OUTSIDE')
                }
              } else if (image.type === 'polygon') {
                let inside = this.inside(mouseX, mouseY, [image.from, ...image.coordinates])
                if (inside) {
                  console.log('INSIDE')
                  image.color = this.deleteColor
                } else {
                  image.color = this.color
                  console.log('OUTSIDE')
                }
              }
            }
          }
        })

        let redraw = false
        for (let i = 0; i < this.images.length; i++) {
          if (this.images[i].color !== images[i].color) {
            redraw = true
            break;
          }
        }
        if (redraw) {
          this.images = images
          this.redraw(true)
        }
      }

    },
    isMouseInLineArea(px, py, x1, y1, x2, y2, threshold = 4) {
      const minX = Math.min(x1, x2) - threshold;
      const maxX = Math.max(x1, x2) + threshold;
      const minY = Math.min(y1, y2) - threshold;
      const maxY = Math.max(y1, y2) + threshold;

      return !(px < minX || px > maxX || py < minY || py > maxY);
    },
    linePointNearestMouse(line, x, y) {
      let dx = line.x1 - line.x0;
      let dy = line.y1 - line.y0;
      let t = ((x - line.x0) * dx + (y - line.y0) * dy) / (dx * dx + dy * dy);
      let lineX = this.lerp(line.x0, line.x1, t);
      let lineY = this.lerp(line.y0, line.y1, t);
      return ({
        x: lineX,
        y: lineY
      });
    },
    inside(x, y, poly) {
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        let xi = poly[i].x, yi = poly[i].y;
        let xj = poly[j].x, yj = poly[j].y;

        let intersect = ((yi > y) !== (yj > y))
            && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }

      return inside;
    },
    lerp(a, b, x) {
      return (a + x * (b - a));
    },
    // Crossing Config Methods
    drawCrossingOverlay(points, color) {
      if (!points || points.length === 0) {
        this.crossingOverlayPoints = []
        this.crossingOverlayColor = null
        this.redraw()
        return
      }
      this.crossingOverlayPoints = points
      this.crossingOverlayColor = color
      this.redraw()
    },
    clearCrossingOverlay() {
      // Only clear temporary overlay, not saved zones
      this.crossingOverlayPoints = []
      this.crossingOverlayColor = null
      this.redraw()
    },
    clearAllCrossingData() {
      // Clear everything including saved zones (used when leaving tab)
      this.crossingOverlayPoints = []
      this.crossingOverlayColor = null
      this.crossingZones = []
      this.crossingWaitingAreas = []
      this.redraw()
    },
    drawAllCrossingZones(zones, waitingAreas, canvasWidth, canvasHeight) {
      this.crossingZones = zones || []
      this.crossingWaitingAreas = waitingAreas || []
      this.crossingCanvasWidth = canvasWidth
      this.crossingCanvasHeight = canvasHeight
      this.redraw()
    },
    drawCrossingElements() {
      // Draw saved zones
      if (this.crossingZones && this.crossingZones.length > 0) {
        const ZONE_COLORS = ['#00ff00', '#0080ff', '#00ffff', '#80ff00', '#00ff80', '#0040ff']
        this.crossingZones.forEach((zone, index) => {
          const color = this.rgbToHex(zone.color) || ZONE_COLORS[index % ZONE_COLORS.length]
          const absPoints = zone.polygon.map(p => ({
            x: Math.round(p[0] * this.width),
            y: Math.round(p[1] * this.height)
          }))

          // Draw polygon
          this.context.strokeStyle = color
          this.context.lineWidth = 2
          this.context.beginPath()
          this.context.moveTo(absPoints[0].x, absPoints[0].y)
          absPoints.slice(1).forEach(p => this.context.lineTo(p.x, p.y))
          this.context.closePath()
          this.context.stroke()
          this.context.fillStyle = color + '20'
          this.context.fill()

          // Draw lines inside polygon
          if (zone.lines && zone.lines.length > 0) {
            this.context.strokeStyle = color + '80'
            this.context.lineWidth = 1
            zone.lines.forEach(line => {
              const x1 = Math.round(line[0][0] * this.width)
              const y1 = Math.round(line[0][1] * this.height)
              const x2 = Math.round(line[1][0] * this.width)
              const y2 = Math.round(line[1][1] * this.height)
              this.context.beginPath()
              this.context.moveTo(x1, y1)
              this.context.lineTo(x2, y2)
              this.context.stroke()
            })
          }

          // Draw label
          this.context.fillStyle = color
          this.context.font = 'bold 12px sans-serif'
          this.context.fillText(zone.name + (zone.geoPolygon ? ' 🗺️' : ''), absPoints[0].x, absPoints[0].y - 8)
        })
      }

      // Draw saved waiting areas
      if (this.crossingWaitingAreas && this.crossingWaitingAreas.length > 0) {
        const WAITING_COLORS = ['#ff8000', '#ff0080', '#ffff00', '#ff00ff', '#ff4000', '#ff0040']
        this.crossingWaitingAreas.forEach((area, index) => {
          const color = this.rgbToHex(area.color) || WAITING_COLORS[index % WAITING_COLORS.length]
          const absPoints = area.polygon.map(p => ({
            x: Math.round(p[0] * this.width),
            y: Math.round(p[1] * this.height)
          }))

          // Draw dashed polygon
          this.context.strokeStyle = color
          this.context.lineWidth = 3
          this.context.setLineDash([8, 4])
          this.context.beginPath()
          this.context.moveTo(absPoints[0].x, absPoints[0].y)
          absPoints.slice(1).forEach(p => this.context.lineTo(p.x, p.y))
          this.context.closePath()
          this.context.stroke()
          this.context.setLineDash([])
          this.context.fillStyle = color + '30'
          this.context.fill()

          // Draw label in center
          if (area.label) {
            const cx = absPoints.reduce((s, p) => s + p.x, 0) / absPoints.length
            const cy = absPoints.reduce((s, p) => s + p.y, 0) / absPoints.length
            this.context.fillStyle = color
            this.context.font = 'bold 28px sans-serif'
            this.context.textAlign = 'center'
            this.context.fillText(area.label, cx, cy + 10)
            this.context.textAlign = 'left'
          }

          // Draw name
          this.context.fillStyle = color
          this.context.font = 'bold 12px sans-serif'
          this.context.fillText(area.name + (area.geoPolygon ? ' 🗺️' : ''), absPoints[0].x, absPoints[0].y - 8)
        })
      }

      // Draw current overlay points
      if (this.crossingOverlayPoints && this.crossingOverlayPoints.length > 0) {
        const color = this.crossingOverlayColor || '#00ff00'

        // Draw points with numbers
        this.crossingOverlayPoints.forEach((p) => {
          this.context.fillStyle = color
          this.context.beginPath()
          this.context.arc(p.x, p.y, 8, 0, Math.PI * 2)
          this.context.fill()

          // Draw number
          this.context.fillStyle = '#000'
          this.context.font = 'bold 11px sans-serif'
          this.context.textAlign = 'center'
          this.context.fillText(p.index.toString(), p.x, p.y + 4)
          this.context.textAlign = 'left'
        })

        // Draw lines between points
        if (this.crossingOverlayPoints.length > 1) {
          this.context.strokeStyle = color
          this.context.lineWidth = 2
          this.context.setLineDash([5, 5])
          this.context.beginPath()
          this.context.moveTo(this.crossingOverlayPoints[0].x, this.crossingOverlayPoints[0].y)
          this.crossingOverlayPoints.slice(1).forEach(p => this.context.lineTo(p.x, p.y))
          this.context.stroke()
          this.context.setLineDash([])
        }

        // Draw closing line preview (faded)
        if (this.crossingOverlayPoints.length >= 3) {
          const last = this.crossingOverlayPoints[this.crossingOverlayPoints.length - 1]
          const first = this.crossingOverlayPoints[0]
          this.context.strokeStyle = color + '40'
          this.context.setLineDash([5, 5])
          this.context.beginPath()
          this.context.moveTo(last.x, last.y)
          this.context.lineTo(first.x, first.y)
          this.context.stroke()
          this.context.setLineDash([])
        }
      }
    },
    rgbToHex(rgb) {
      if (!rgb) return null
      if (typeof rgb === 'string') return rgb
      if (Array.isArray(rgb)) {
        return '#' + rgb.map(x => x.toString(16).padStart(2, '0')).join('')
      }
      return null
    }
  },
  render() {
    return h('canvas', {
      attrs: {
        id: this.canvasId,
        width: Number(this.width),
        height: Number(this.height)
      },
      style: {
        'touchAction': 'none',
        // @ts-ignore
        ...this.styles
      },
      class: this.classes,
      on: {
        mousedown: (event) => this.startDraw(event),
        mousemove: (event) => {
          this.draw(event);
          this.handleMousemove(event);
        },
        mouseup: () => this.stopDraw(false, 'mouseup'),
        //mouseleave: () => this.stopDraw(true, 'mouseleave'),
        touchstart: (event) => this.startDraw(event),
        touchmove: (event) => this.draw(event),
        touchend: () => this.stopDraw(false, 'touchend'),
        touchleave: () => this.stopDraw(true, 'touchlive'),
        touchcancel: () => this.stopDraw(false, 'touchcancel'),
        pointerdown: (event) => this.startDraw(event),
        pointermove: (event) => this.draw(event),
        pointerup: () => this.stopDraw(false, 'pointerup'),
        //pointerleave: () => this.stopDraw(true, 'pointerleave'),
        pointercancel: () => this.stopDraw(true, 'pointercancel'),
      },
      ...this.$props
    })
  }
});
</script>

<style scoped>

</style>